import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Return } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { OrderLink } from '@/features/orders';

import { ReturnResolutionLabel, ReturnStatusBadge } from './return-status-badge';

/** Column model of the returns table; `meta` ties columns to the list's sorts and filters. */
export function useReturnColumns(storeId: string) {
  const { t } = useTranslation('returns');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Return>>>(
    () => [
      {
        id: 'rma',
        header: t('columns.rma'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/returns/$returnId"
            params={{ storeId, returnId: row.original.id }}
            className="font-mono text-[13px] font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.rmaNumber}
          </Link>
        ),
        meta: { label: t('columns.rma'), lockVisibility: true },
        size: 120,
      },
      {
        id: 'order',
        header: t('columns.order'),
        cell: ({ row }) => (
          <OrderLink
            storeId={storeId}
            orderId={row.original.orderId}
            name={row.original.orderName}
          />
        ),
        meta: { label: t('columns.order'), filterKey: 'orderId' },
        size: 120,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <ReturnStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status' },
        size: 120,
      },
      {
        id: 'resolution',
        accessorKey: 'resolution',
        header: t('columns.resolution'),
        cell: ({ row }) => <ReturnResolutionLabel resolution={row.original.resolution} />,
        meta: { filterKey: 'resolution' },
        size: 130,
      },
      {
        id: 'units',
        header: t('columns.units'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('unitCount', {
              count: row.original.lines.reduce((sum, line) => sum + line.quantity, 0),
            })}
          </span>
        ),
        meta: { label: t('columns.units'), align: 'end' },
        size: 90,
      },
      {
        id: 'requestedAt',
        accessorKey: 'requestedAt',
        header: t('columns.requested'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.requestedAt)}>
            {format.relative(row.original.requestedAt)}
          </span>
        ),
        meta: { sortField: 'requestedAt', filterKey: 'requestedAt' },
        size: 130,
      },
    ],
    [format, storeId, t],
  );
}
